/**
 * @file delegatedTaskService.ts
 * @brief Delegated tasks API service. One-on-one task delegation (no RACI/project).
 * Uses backend /task_management/delegated-tasks endpoints; returns unwrapped payloads.
 */

import apiClient, { unwrapResponse } from './apiClient';
import { API_ENDPOINTS } from './endpoints';
import {
  DelegatedTaskStatus,
  type DelegatedTask,
  type DelegatedTaskFeedback,
  type PaginatedResponse,
  type TaskPriority,
} from '@/types';

/** Map backend DTO to frontend DelegatedTask. Backend uses camelCase; display names not in API. */
function mapBackendToDelegatedTask(raw: Record<string, unknown>): DelegatedTask {
  const feedback = (raw.feedback as Record<string, unknown>[] | undefined) ?? [];
  return {
    id: String(raw.id ?? ''),
    title: String(raw.title ?? ''),
    description: raw.description != null ? String(raw.description) : undefined,
    status: (raw.status as DelegatedTaskStatus) ?? ('pending' as DelegatedTaskStatus),
    priority: (raw.priority as TaskPriority) ?? ('p3' as TaskPriority),
    assignedTo: String(raw.assignedTo ?? ''),
    assignedBy: String(raw.assignedBy ?? ''),
    assignedToName: raw.assignedToName != null ? String(raw.assignedToName) : undefined,
    assignedByName: raw.assignedByName != null ? String(raw.assignedByName) : undefined,
    startDate: raw.startDate != null ? String(raw.startDate) : undefined,
    dueDate: String(raw.dueDate ?? ''),
    completedAt: raw.completedAt != null ? String(raw.completedAt) : undefined,
    acceptedAt: raw.acceptedAt != null ? String(raw.acceptedAt) : undefined,
    rejectedAt: raw.rejectedAt != null ? String(raw.rejectedAt) : undefined,
    rejectionReason: raw.rejectionReason != null ? String(raw.rejectionReason) : undefined,
    estimatedMinutes: raw.estimatedMinutes != null ? Number(raw.estimatedMinutes) : undefined,
    actualMinutes: raw.actualMinutes != null ? Number(raw.actualMinutes) : undefined,
    progressPercentage: raw.progressPercentage != null ? Number(raw.progressPercentage) : undefined,
    notes: raw.notes != null ? String(raw.notes) : undefined,
    completionNotes: raw.completionNotes != null ? String(raw.completionNotes) : undefined,
    isUrgent: Boolean(raw.isUrgent),
    tags: Array.isArray(raw.tags) ? (raw.tags as string[]) : undefined,
    feedback: feedback.map((f): DelegatedTaskFeedback => ({
      id: String(f.id ?? ''),
      content: String(f.content ?? ''),
      rating: f.rating != null ? Number(f.rating) : undefined,
      createdAt: String(f.createdAt ?? ''),
      createdBy: String(f.createdBy ?? ''),
    })),
    attachments: Array.isArray(raw.attachments) ? (raw.attachments as DelegatedTask['attachments']) : undefined,
    createdAt: String(raw.createdAt ?? ''),
    updatedAt: String(raw.updatedAt ?? ''),
    createdBy: raw.createdBy != null ? String(raw.createdBy) : undefined,
  };
}

function mapListResponse(raw: { items?: unknown[]; total?: number; page?: number; pageSize?: number; totalPages?: number; hasMore?: boolean }): PaginatedResponse<DelegatedTask> {
  const items = Array.isArray(raw?.items) ? raw.items.map((r) => mapBackendToDelegatedTask(r as Record<string, unknown>)) : [];
  return {
    items,
    total: Number(raw?.total ?? 0),
    page: Number(raw?.page ?? 1),
    pageSize: Number(raw?.pageSize ?? 20),
    totalPages: Number(raw?.totalPages ?? 1),
    hasMore: Boolean(raw?.hasMore),
  };
}

export const delegatedTaskService = {
  /**
   * Get tasks delegated BY the current user (filter=delegated).
   * Returns unwrapped PaginatedResponse<DelegatedTask>.
   */
  async getDelegatedTasks(
    userId: string,
    options?: { page?: number; pageSize?: number; status?: string }
  ): Promise<PaginatedResponse<DelegatedTask>> {
    const params = new URLSearchParams();
    params.set('filter', 'delegated');
    if (options?.page != null) params.set('page', String(options.page));
    if (options?.pageSize != null) params.set('pageSize', String(options.pageSize));
    if (options?.status) params.set('status', options.status);
    const url = `${API_ENDPOINTS.DELEGATED_TASKS.LIST}?${params.toString()}`;
    const response = await apiClient.get(url);
    const data = unwrapResponse<{ items: Record<string, unknown>[]; total: number; page: number; pageSize: number; totalPages: number; hasMore: boolean }>(response);
    return mapListResponse(data);
  },

  /**
   * Get tasks assigned TO the current user (filter=assigned).
   * Returns unwrapped PaginatedResponse<DelegatedTask>.
   */
  async getAssignedTasks(
    userId: string,
    options?: { page?: number; pageSize?: number; status?: string }
  ): Promise<PaginatedResponse<DelegatedTask>> {
    const params = new URLSearchParams();
    params.set('filter', 'assigned');
    if (options?.page != null) params.set('page', String(options.page));
    if (options?.pageSize != null) params.set('pageSize', String(options.pageSize));
    if (options?.status) params.set('status', options.status);
    const url = `${API_ENDPOINTS.DELEGATED_TASKS.LIST}?${params.toString()}`;
    const response = await apiClient.get(url);
    const data = unwrapResponse<{ items: Record<string, unknown>[]; total: number; page: number; pageSize: number; totalPages: number; hasMore: boolean }>(response);
    return mapListResponse(data);
  },

  /**
   * Get a single delegated task by ID. Returns unwrapped DelegatedTask.
   */
  async getDelegatedTask(taskId: string): Promise<DelegatedTask> {
    const response = await apiClient.get(API_ENDPOINTS.DELEGATED_TASKS.GET(taskId));
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapBackendToDelegatedTask(data);
  },

  /**
   * Create a delegated task. Body: assignedTo, title, description?, priority?, dueDate?, estimatedMinutes?, notes?, isUrgent?.
   * Returns unwrapped DelegatedTask.
   */
  async createDelegatedTask(data: Partial<DelegatedTask>): Promise<DelegatedTask> {
    const body: Record<string, unknown> = {
      assignedTo: data.assignedTo,
      title: data.title ?? 'Untitled Task',
      description: data.description ?? undefined,
      priority: data.priority ?? 'p3',
      dueDate: data.dueDate ?? undefined,
      estimatedMinutes: data.estimatedMinutes ?? undefined,
      notes: data.notes ?? undefined,
      isUrgent: data.isUrgent ?? false,
    };
    const response = await apiClient.post(API_ENDPOINTS.DELEGATED_TASKS.CREATE, body);
    const raw = unwrapResponse<Record<string, unknown>>(response);
    return mapBackendToDelegatedTask(raw);
  },

  /**
   * Update a delegated task. Returns unwrapped DelegatedTask.
   */
  async updateDelegatedTask(taskId: string, data: Partial<DelegatedTask>): Promise<DelegatedTask> {
    const body: Record<string, unknown> = {
      title: data.title,
      description: data.description,
      priority: data.priority,
      dueDate: data.dueDate,
      estimatedMinutes: data.estimatedMinutes,
      notes: data.notes,
      completionNotes: data.completionNotes,
      isUrgent: data.isUrgent,
    };
    const response = await apiClient.put(API_ENDPOINTS.DELEGATED_TASKS.UPDATE(taskId), body);
    const raw = unwrapResponse<Record<string, unknown>>(response);
    return mapBackendToDelegatedTask(raw);
  },

  /**
   * Update delegated task status. Optional data: rejectionReason (for rejected), completionNotes (for completed).
   * Returns unwrapped DelegatedTask.
   */
  async updateDelegatedTaskStatus(
    taskId: string,
    status: DelegatedTaskStatus,
    data?: Record<string, unknown>
  ): Promise<DelegatedTask> {
    const body: Record<string, unknown> = { status };
    if (data?.rejectionReason !== undefined) body.rejectionReason = data.rejectionReason;
    if (data?.completionNotes !== undefined) body.completionNotes = data.completionNotes;
    const response = await apiClient.patch(API_ENDPOINTS.DELEGATED_TASKS.STATUS(taskId), body);
    const raw = unwrapResponse<Record<string, unknown>>(response);
    return mapBackendToDelegatedTask(raw);
  },

  /**
   * Update task progress (0–100). Returns unwrapped DelegatedTask.
   */
  async updateProgress(taskId: string, progress: number): Promise<DelegatedTask> {
    const response = await apiClient.patch(API_ENDPOINTS.DELEGATED_TASKS.PROGRESS(taskId), { progress });
    const raw = unwrapResponse<Record<string, unknown>>(response);
    return mapBackendToDelegatedTask(raw);
  },

  /**
   * Add feedback to a delegated task. Returns full task (refetched) so UI has updated feedback list.
   */
  async addFeedback(taskId: string, feedback: Partial<DelegatedTaskFeedback>): Promise<DelegatedTask> {
    await apiClient.post(API_ENDPOINTS.DELEGATED_TASKS.FEEDBACK(taskId), {
      content: feedback.content ?? '',
      rating: feedback.rating,
    });
    return this.getDelegatedTask(taskId);
  },

  /**
   * Delete a delegated task. Returns void.
   */
  async deleteDelegatedTask(taskId: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.DELEGATED_TASKS.DELETE(taskId));
  },

  /**
   * Get delegated task statistics (delegated vs assigned counts). Computed from list responses.
   */
  async getStats(userId: string): Promise<{
    delegated: { total: number; pending: number; completed: number; overdue: number };
    assigned: { total: number; pending: number; completed: number; overdue: number };
  }> {
    const [delegatedRes, assignedRes] = await Promise.all([
      this.getDelegatedTasks(userId, { page: 1, pageSize: 500 }),
      this.getAssignedTasks(userId, { page: 1, pageSize: 500 }),
    ]);
    const now = new Date().toISOString();
    const calc = (items: DelegatedTask[]) => ({
      total: items.length,
      pending: items.filter((t) =>
        t.status === DelegatedTaskStatus.PENDING ||
        t.status === DelegatedTaskStatus.ACCEPTED ||
        t.status === DelegatedTaskStatus.IN_PROGRESS
      ).length,
      completed: items.filter((t) => t.status === DelegatedTaskStatus.COMPLETED).length,
      overdue: items.filter(
        (t) =>
          t.dueDate < now &&
          t.status !== DelegatedTaskStatus.COMPLETED &&
          t.status !== DelegatedTaskStatus.CANCELLED
      ).length,
    });
    return {
      delegated: calc(delegatedRes.items),
      assigned: calc(assignedRes.items),
    };
  },
};

export default delegatedTaskService;
